import React, { Component } from 'react';
import SectionHeader from '../../components/views/SectionHeader';
import TabsContainer from '../../components/views/TabsContainer';
import API from '../../utils/API';
import LoadingWizard from '../../components/spinner/wizard';

class SourcedVenueDetails extends Component {
	constructor(props) {
		super(props);
		this.state = {
			selectedSection: 0,
			selectedSectionId: 'info',
			placeDetails: null,
			loadingPlaceDetails: false,
			placeDetailsError: null
		};
	}

	async componentDidMount() {
		// Fetch Google Places details if placeId exists
		if (this.props.venue.placeId && this.props.venue.source === 'google_places') {
			this.fetchPlaceDetails();
		}
	}

	fetchPlaceDetails = async () => {
		this.setState({ loadingPlaceDetails: true });
		try {
			const response = await API().post(
				`Organizations/${this.props.orgId}/sourced-venues/place-details`,
				{ placeId: this.props.venue.placeId }
			);
			
			if (response.data && response.data.success) {
				this.setState({ 
					placeDetails: response.data,
					loadingPlaceDetails: false 
				});
			} else {
				this.setState({ 
					placeDetailsError: response.data?.error || 'Failed to fetch place details',
					loadingPlaceDetails: false 
				});
			}
		} catch (error) {
			console.error('Error fetching place details:', error);
			this.setState({ 
				placeDetailsError: 'Failed to fetch place details',
				loadingPlaceDetails: false 
			});
		}
	};

	updateSelected = (selected, tabId) => {
		this.setState({ selectedSection: selected, selectedSectionId: tabId });
	};

	renderInfoField = (label, value, icon = null, link = null) => {
		if (!value) return null;
		
		return (
			<div className="infoField mb-20">
				<label className="formFieldLabel">{label}</label>
				<div className="infoValue">
					{icon && <i className={`${icon} mr-10`} />}
					{link ? (
						<a href={link} target="_blank" rel="noopener noreferrer">{value}</a>
					) : (
						<span>{value}</span>
					)}
				</div>
			</div>
		);
	};

	renderSection = () => {
		const { selectedSectionId, placeDetails, loadingPlaceDetails } = this.state;
		const { venue } = this.props;

		let sectionBlock;
		switch (selectedSectionId) {
			case 'info':
				// Use live Google Places data if available, otherwise use stored data
				const displayData = placeDetails || venue;
				const hasLiveData = placeDetails && placeDetails.success;

				sectionBlock = (
					<div className="sectionBody">
						{loadingPlaceDetails && (
							<LoadingWizard loading={true} text="Fetching latest venue details from Google Places..." />
						)}

						<div className='sectionBlock'>
							<h2 className='blockHeader'>Basic Information</h2>
							
							{this.renderInfoField('Venue Name', displayData.name || venue.name)}
							{this.renderInfoField('Venue Type', venue.venueType || 'Physical')}
							{this.renderInfoField(
								'Location', 
								hasLiveData ? displayData.formatted_address : (venue.location || 'Not specified'),
								'las la-map-marker-alt'
							)}
							
							{hasLiveData && displayData.url && (
								<div className="infoField mb-20">
									<label className="formFieldLabel">Google Maps</label>
									<div className="infoValue">
										<a href={displayData.url} target="_blank" rel="noopener noreferrer">
											<i className="las la-external-link-alt mr-10" />
											View on Google Maps
										</a>
									</div>
								</div>
							)}

							{hasLiveData && displayData.rating && (
								<div className="infoField mb-20">
									<label className="formFieldLabel">Rating</label>
									<div className="infoValue">
										<span style={{ color: '#f5a623', fontSize: '1.2rem' }}>
											{'★'.repeat(Math.round(displayData.rating))}
											{'☆'.repeat(5 - Math.round(displayData.rating))}
										</span>
										<span className="calibreBold ml-10">
											{displayData.rating.toFixed(1)}
										</span>
										{displayData.user_ratings_total && (
											<span className="grey ml-5">
												({displayData.user_ratings_total.toLocaleString()} reviews)
											</span>
										)}
									</div>
								</div>
							)}

							{hasLiveData && displayData.business_status && (
								<div className="infoField mb-20">
									<label className="formFieldLabel">Status</label>
									<div className="infoValue">
										<span className={displayData.business_status === 'OPERATIONAL' ? 'badge-success' : 'badge-warning'}>
											{displayData.business_status === 'OPERATIONAL' ? 'Open' : displayData.business_status}
										</span>
									</div>
								</div>
							)}

							{venue.description && this.renderInfoField('Description', venue.description)}

							<div className="infoField mb-20">
								<label className="formFieldLabel">Source</label>
								<div className="infoValue">
									{venue.source === 'google_places' ? (
										<span>
											<i className="lab la-google mr-10" />
											Google Places
											{hasLiveData && <span className="grey ml-10">(Live Data)</span>}
										</span>
									) : (
										venue.source
									)}
								</div>
							</div>
						</div>

						{/* Opening Hours */}
						{hasLiveData && displayData.opening_hours && displayData.opening_hours.weekday_text && displayData.opening_hours.weekday_text.length > 0 && (
							<div className='sectionBlock'>
								<h2 className='blockHeader'>Business Hours</h2>
								{displayData.opening_hours.open_now !== undefined && (
									<p className="mb-15">
										<span className={displayData.opening_hours.open_now ? 'badge-success' : 'badge-danger'}>
											{displayData.opening_hours.open_now ? 'Open Now' : 'Closed'}
										</span>
									</p>
								)}
								<ul style={{ listStyle: 'none', padding: 0 }}>
									{displayData.opening_hours.weekday_text.map((day, index) => (
										<li key={index} className="mb-5">{day}</li>
									))}
								</ul>
							</div>
						)}

						<div className='sectionBlock'>
							<h2 className='blockHeader'>Contact Information</h2>
							
							{this.renderInfoField(
								'Phone Number',
								hasLiveData ? displayData.phone_number : venue.phoneNumber,
								'las la-phone',
								`tel:${hasLiveData ? displayData.phone_number : venue.phoneNumber}`
							)}

							{this.renderInfoField(
								'Email',
								venue.contactEmail,
								'las la-envelope',
								`mailto:${venue.contactEmail}`
							)}

							{this.renderInfoField(
								'Website',
								hasLiveData ? (displayData.website ? 'Visit Website' : null) : (venue.website ? 'Visit Website' : null),
								'las la-globe',
								hasLiveData ? displayData.website : venue.website
							)}

							{!displayData.phone_number && !venue.phoneNumber && !venue.contactEmail && !displayData.website && !venue.website && (
								<p className="grey">No contact information available</p>
							)}
						</div>

						<div className='sectionBlock'>
							<h2 className='blockHeader'>Capacity Information</h2>
							
							{venue.capacity?.max > 0 ? (
								<div className="flex wrap" style={{ gap: '30px' }}>
									{venue.capacity.max > 0 && (
										<div>
											<label className="formFieldLabel">Max Capacity</label>
											<p className="calibreBold mb-0" style={{ fontSize: '2rem' }}>
												{venue.capacity.max}
											</p>
										</div>
									)}
									{venue.capacity.theater > 0 && (
										<div>
											<label className="formFieldLabel">Theater Style</label>
											<p className="calibreBold mb-0" style={{ fontSize: '2rem' }}>
												{venue.capacity.theater}
											</p>
										</div>
									)}
									{venue.capacity.classroom > 0 && (
										<div>
											<label className="formFieldLabel">Classroom Style</label>
											<p className="calibreBold mb-0" style={{ fontSize: '2rem' }}>
												{venue.capacity.classroom}
											</p>
										</div>
									)}
									{venue.capacity.banquet > 0 && (
										<div>
											<label className="formFieldLabel">Banquet Style</label>
											<p className="calibreBold mb-0" style={{ fontSize: '2rem' }}>
												{venue.capacity.banquet}
											</p>
										</div>
									)}
								</div>
							) : (
								<p className="grey">No capacity information available</p>
							)}
						</div>

						{venue.amenities && venue.amenities.length > 0 && (
							<div className='sectionBlock'>
								<h2 className='blockHeader'>Amenities & Features</h2>
								<div className="flex flex-wrap" style={{ gap: '8px' }}>
									{venue.amenities.map((amenity, index) => (
										<span
											key={index}
											className="badge"
											style={{
												padding: '8px 12px',
												backgroundColor: 'var(--light-grey)',
												borderRadius: '4px'
											}}
										>
											<i className="las la-check mr-5" style={{ color: 'var(--success)' }} />
											{amenity}
										</span>
									))}
								</div>
							</div>
						)}

						{/* Photo Gallery */}
						{hasLiveData && placeDetails.photos && placeDetails.photos.length > 0 && (
							<div className='sectionBlock'>
								<h2 className='blockHeader'>Photos</h2>
								<div style={{ 
									display: 'grid', 
									gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
									gap: '15px' 
								}}>
									{placeDetails.photos.slice(0, 6).map((photo, index) => (
										<img
											key={index}
											src={photo.url}
											alt={`${venue.name} ${index + 1}`}
											style={{
												width: '100%',
												height: '150px',
												objectFit: 'cover',
												borderRadius: '4px',
												cursor: 'pointer'
											}}
											onClick={() => window.open(photo.url, '_blank')}
										/>
									))}
								</div>
							</div>
						)}

						{/* Reviews Section */}
						{hasLiveData && placeDetails.reviews && placeDetails.reviews.length > 0 && (
							<div className='sectionBlock'>
								<h2 className='blockHeader'>Recent Reviews</h2>
								{placeDetails.reviews.slice(0, 3).map((review, index) => (
									<div key={index} className="mb-20 pb-20" style={{ borderBottom: '1px solid var(--border-color)' }}>
										<div className="flex aic jcsb mb-10">
											<div className="flex aic">
												{review.profile_photo_url && (
													<img 
														src={review.profile_photo_url} 
														alt={review.author_name}
														style={{
															width: '40px',
															height: '40px',
															borderRadius: '50%',
															marginRight: '10px'
														}}
													/>
												)}
												<div>
													<p className="calibreBold mb-0">{review.author_name}</p>
													<p className="small grey mb-0">{review.relative_time_description}</p>
												</div>
											</div>
											<div style={{ color: '#f5a623' }}>
												{'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
											</div>
										</div>
										<p className="mb-0">{review.text}</p>
									</div>
								))}
							</div>
						)}
					</div>
				);
				break;
			default:
		}

		return sectionBlock;
	};

	render() {
		const { selectedSection } = this.state;
		const { venue } = this.props;

		const tabs = [{ title: <span>Venue Information</span>, id: 'info' }];

		return (
			<div className="sectionContainer">
				<SectionHeader
					breadcrumbs={[{ name: venue.name }]}
					title="Venue Details"
					description="View venue information from your sourced listing"
				>
					<TabsContainer
						tabs={tabs}
						updateSelected={this.updateSelected}
						selectedIndex={selectedSection}
					/>
				</SectionHeader>
				{this.renderSection()}
			</div>
		);
	}
}

export default SourcedVenueDetails;
